import { useState, useEffect } from 'react';


import { mergeDuplicateCustomers } from '../utils';
import { Vehicle } from './useVehicleManagement';
import { ToastType } from './useToast';
import { vrpApiService } from '../../../../api/mobiApi/vrp-api.service';
import { Customer, Route } from '../../../../api/mobiApi/domain/domain.types';
import { VRPConstraints } from '../components/VRPConstraintsModal';

interface UseRouteManagementProps {
  selectedCustomers: Customer[];
  selectedVehicles: Vehicle[];
  depot: { lat: number; lon: number; name: string; demand: number }; // Start depot for backward compatibility
  endDepot?: { lat: number; lon: number; name: string; demand: number }; // End depot (optional)
  showToast: (type: ToastType, message: string) => void;
  constraints?: VRPConstraints;
}

interface SolutionStats {
  totalRoutes: number;
  totalDistance: number;
  totalTime: number;
  vehiclesUsed: number;
}

export const useRouteManagement = ({
  selectedCustomers,
  selectedVehicles,
  depot,
  endDepot,
  showToast,
  constraints = {}
}: UseRouteManagementProps) => {
  // Extract values from constraints with defaults
  const startTime = constraints.startTime || '08:00';
  const endTime = constraints.endTime || '18:00';
  const lunchBreak = constraints.lunchBreak !== undefined ? constraints.lunchBreak : true;
  const lunchBreakStart = constraints.lunchBreakStart || '12:30';
  const lunchBreakEnd = constraints.lunchBreakEnd || '13:30';
  const serviceDuration = constraints.serviceDuration || 2400;
  const [routes, setRoutes] = useState<Route[]>([]);
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null);
  const [manuallyClosedRoute, setManuallyClosedRoute] = useState(false);
  const [solutionStats, setSolutionStats] = useState<SolutionStats>({
    totalRoutes: 0,
    totalDistance: 0,
    totalTime: 0,
    vehiclesUsed: 0
  });
  const [loading, setLoading] = useState(false);
  const [solverType, setSolverType] = useState<'vroom' | 'optaplanner'>('vroom');

  // Select first route when routes are generated (unless manually closed)
  useEffect(() => {
    if (routes.length > 0 && !selectedRoute && !manuallyClosedRoute) {
      setSelectedRoute(routes[0]);
    }
  }, [routes, selectedRoute, manuallyClosedRoute]);

  const solveVRP = async () => {
    if (selectedCustomers.length === 0 || selectedVehicles.length === 0) {
      showToast('warning', 'Lütfen en az bir müşteri ve bir araç seçin');
      return;
    }

    setLoading(true);
    try {
      const mergedCustomers = mergeDuplicateCustomers(selectedCustomers);

      if (mergedCustomers.length < selectedCustomers.length) {
        const duplicateCount = selectedCustomers.length - mergedCustomers.length;
        console.log(`✅ ${duplicateCount} müşteri birleştirildi (${selectedCustomers.length} → ${mergedCustomers.length})`);
        showToast('info', `${duplicateCount} tekrar eden müşteri birleştirildi`);
      }

      // Build locations array with start depot, customers, and end depot (if different)
      const locations: any[] = [
        {
          lat: depot.lat,
          lon: depot.lon,
          location_type: 'start_depot',
          demand: 0,
          name: depot.name || 'Başlangıç Deposu'
        },
        ...mergedCustomers.map(c => ({
          lat: c.lat,
          lon: c.lon,
          location_type: 'customer',
          demand: c.demand || 0,
          name: c.name,
          ...(c.time_window && {
            time_window: {
              start: c.time_window.start,
              end: c.time_window.end
            }
          }),
          ...(c.priority !== undefined && c.priority !== null && {
            priority: c.priority
          })
        }))
      ];

      // Add end depot if different from start depot
      if (endDepot) {
        locations.push({
          lat: endDepot.lat,
          lon: endDepot.lon,
          location_type: 'end_depot',
          demand: 0,
          name: endDepot.name || 'Bitiş Deposu'
        });
      }

      // Constraints objesi oluştur (sadece tanımlı değerleri ekle)
      const constraintsPayload: any = {};

      // 1. Zaman & Mesafe Kısıtlamaları
      if (constraints.maxWorkingHours !== undefined) constraintsPayload.maxWorkingHours = constraints.maxWorkingHours;
      if (constraints.maxDistance !== undefined) constraintsPayload.maxDistance = constraints.maxDistance;

      // 2. Görev Kısıtlamaları (Task Constraints)
      if (constraints.maxTasks !== undefined && constraints.maxTasks !== null) constraintsPayload.maxTasks = constraints.maxTasks;

      // 3. Mola Kısıtlamaları (Break Constraints)
      if (constraints.enableBreaks !== undefined) constraintsPayload.enableBreaks = constraints.enableBreaks;
      if (constraints.breakDuration !== undefined) constraintsPayload.breakDuration = constraints.breakDuration;
      if (constraints.breakAfterHours !== undefined) constraintsPayload.breakAfterHours = constraints.breakAfterHours;

      // 4. Öncelik Kısıtlamaları (Priority Constraints)
      if (constraints.enablePriority !== undefined) constraintsPayload.enablePriority = constraints.enablePriority;
      if (constraints.priorityWeight !== undefined) constraintsPayload.priorityWeight = constraints.priorityWeight;

      // 5. Yetenek/Beceri Kısıtlamaları (Skills Constraints)
      if (constraints.enableSkills !== undefined) constraintsPayload.enableSkills = constraints.enableSkills;

      // 6. Hız Kısıtlamaları (Speed Constraints)
      if (constraints.speedFactor !== undefined) constraintsPayload.speedFactor = constraints.speedFactor;

      // 7. Yol Kısıtlamaları (Route Preferences)
      if (constraints.avoidTolls !== undefined) constraintsPayload.avoidTolls = constraints.avoidTolls;
      if (constraints.avoidHighways !== undefined) constraintsPayload.avoidHighways = constraints.avoidHighways;
      if (constraints.avoidTunnels !== undefined) constraintsPayload.avoidTunnels = constraints.avoidTunnels;
      if (constraints.avoidFerries !== undefined) constraintsPayload.avoidFerries = constraints.avoidFerries;

      // 8. Optimizasyon Hedefi
      if (constraints.optimizationGoal !== undefined) constraintsPayload.optimizationGoal = constraints.optimizationGoal;

      // Yeni API formatına uygun request (sadece zorunlu ve opsiyonel alanlar)
      const request = {
        locations,
        vehicles: selectedVehicles.map((v, index) => ({
          id: v.id || `vehicle_${String(index + 1).padStart(3, '0')}`,
          capacity: v.capacity || 500,
          type: v.type || 'truck',
          name: v.name,
          // Araç Özel Parametreler
          ...(v.skills && v.skills.length > 0 && { skills: v.skills }),
          ...(v.speed_factor !== undefined && { speed_factor: v.speed_factor })
        })),
        backend: 'valhalla' as const,
        solver_type: 'vroom' as const,
        use_route_optimization: true,
        start_time: startTime,
        end_time: endTime,
        service_duration: serviceDuration,
        lunch_break: lunchBreak,
        ...(lunchBreak && {
          lunch_break_start: lunchBreakStart,
          lunch_break_end: lunchBreakEnd
        }),
        // Kısıtlamaları Kaldır parametresi
        ...(constraints.ignoreConstraints !== undefined && {
          ignoreConstraints: constraints.ignoreConstraints
        }),
        // Kısıtlamaları ekle (sadece varsa ve ignoreConstraints false ise)
        ...(Object.keys(constraintsPayload).length > 0 && !constraints.ignoreConstraints && {
          constraints: constraintsPayload
        })
      };

      console.log('🚀 VRP API Request:', JSON.stringify(request, null, 2));

      const response = await vrpApiService.solveVRP(request);

      if (response.error) {
        throw new Error(response.error);
      }

      if (response.data && response.data.cars) {
        const processedRoutes = response.data.cars.map((car: any) => {
          const sortedRouteSequence = (car.route_sequence || []).sort((a: any, b: any) => {
            return (a.sequence_order || 0) - (b.sequence_order || 0);
          });

          // Enrich route_sequence with customer data (address, time_window)
          const enrichedRouteSequence = sortedRouteSequence.map((step: any) => {
            if (step.location_type === 'CUSTOMER' || step.location_type === 'customer') {
              // Try multiple matching strategies
              const matchedCustomer = selectedCustomers.find(c =>
                c.name === step.customer_name ||
                c.name === step.name ||
                c.id === step.customer_id ||
                (Math.abs(c.lat - step.lat) < 0.00001 && Math.abs(c.lon - step.lon) < 0.00001)
              );

              if (matchedCustomer) {
                console.log(`✅ Matched customer: ${matchedCustomer.name}`, {
                  id: matchedCustomer.id,
                  address: matchedCustomer.address,
                  time_window: matchedCustomer.time_window
                });
                return {
                  ...step,
                  customer_id: matchedCustomer.id,
                  name: matchedCustomer.name,
                  address: matchedCustomer.address,
                  time_window: matchedCustomer.time_window
                };
              } else {
                console.warn(`⚠️ No match found for step:`, step);
              }
            }
            return step;
          });

          const customerSteps = enrichedRouteSequence.filter((step: any) =>
            step.location_type === 'CUSTOMER' || step.location_type === 'customer'
          );

          const customerIds = customerSteps.map((step: any) => {
              if (step.customer_id) {
                return step.customer_id;
              }

              const matchedCustomer = selectedCustomers.find(c =>
                c.name === step.customer_name || c.name === step.name
              );

              return matchedCustomer?.id;
            })
            .filter((id: any) => id !== undefined);

          console.log('📦 Processing route for', car.vehicle_id, '- Customers:', customerIds);

          return {
            id: car.vehicle_id,
            vehicleId: parseInt(car.vehicle_id.replace('vehicle_', '')),
            vehicle_id: car.vehicle_id,
            vehicle_type: car.vehicle_type,
            route_sequence: enrichedRouteSequence,
            raw_shapes: car.raw_shapes || [],
            raw_shape: car.raw_shape || '',
            distance: car.distance || 0,
            route_time: car.route_time || 0,
            start_time: car.start_time,
            end_time: car.end_time,
            customers_count: customerIds.length,
            total_demand: car.total_demand || 0,
            vehicle_capacity: car.vehicle_capacity || 0,
            customers: customerIds,
            totalTime: car.route_time || 0,
            totalDistance: car.distance || 0,
          };
        }) as Route[];

        setRoutes(processedRoutes);
        setSelectedRoute(null); // No route selected by default
        setManuallyClosedRoute(true); // Prevent auto-selection

        const stats = {
          totalRoutes: processedRoutes.length,
          totalDistance: processedRoutes.reduce((sum: number, r: any) => sum + (r.distance || 0), 0),
          totalTime: processedRoutes.reduce((sum: number, r: any) => sum + (r.route_time || 0), 0),
          vehiclesUsed: processedRoutes.length
        };
        setSolutionStats(stats);

        showToast('success', `${processedRoutes.length} rota oluşturuldu`);
        return true; // Success indicator
      }
    } catch (error: any) {
      console.error('❌ VRP çözüm hatası:', error);
      showToast('error', error.message || 'VRP çözümü başarısız oldu');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const reorderCustomers = async (fromIndex: number, toIndex: number) => {
    if (!selectedRoute || fromIndex === toIndex) return;

    try {
      const updatedRoutes = [...routes];
      const routeIndex = updatedRoutes.findIndex(r => r.vehicle_id === selectedRoute.vehicle_id);

      if (routeIndex === -1) return;

      const route = updatedRoutes[routeIndex];

      if (!route.route_sequence || route.route_sequence.length === 0) {
        console.error('⚠️ No route sequence found');
        showToast('error', 'Rota bilgisi bulunamadı');
        return;
      }

      const newSequence = [...route.route_sequence];
      const customerSteps = newSequence.filter((s: any) =>
        s && (s.location_type === 'CUSTOMER' || s.location_type === 'customer')
      );

      if (fromIndex < 0 || fromIndex >= customerSteps.length ||
          toIndex < 0 || toIndex >= customerSteps.length) {
        console.error('Invalid index for reordering', { fromIndex, toIndex, length: customerSteps.length });
        showToast('error', 'Geçersiz sıralama işlemi');
        return;
      }

      const [movedCustomer] = customerSteps.splice(fromIndex, 1);
      if (!movedCustomer) {
        console.error('Could not find customer to move');
        return;
      }

      customerSteps.splice(toIndex, 0, movedCustomer);

      const depotStr = localStorage.getItem('mobiroute_depot');
      const depotData = depotStr ? JSON.parse(depotStr) : depot;

      const editRequest = {
        vehicles: selectedVehicles,
        edits: customerSteps.map((step: any) => ({
          customer_lat: step.lat,
          customer_lon: step.lon,
          customer_demand: step.demand || 0,
          from_vehicle_id: route.vehicle_id,
          to_vehicle_id: route.vehicle_id,
          customer_id: step.customer_id || step.id || '',
          customer_name: step.customer_name || step.name || ''
        })),
        use_route_optimization: false,
        is_reorder_only: true,
        depot_lat: depotData.lat,
        depot_lon: depotData.lon
      };

      console.log('🔧 Sending edit route request:', editRequest);
      showToast('info', 'Rota güncelleniyor...');

      const response = await vrpApiService.editRoute(editRequest);

      if (response.data) {
        console.log('✅ Route updated successfully:', response.data);

        const updatedCar = response.data.cars.find((car: any) => car.vehicle_id === route.vehicle_id);
        if (updatedCar) {
          updatedRoutes[routeIndex] = {
            id: updatedCar.vehicle_id,
            vehicleId: parseInt(updatedCar.vehicle_id.replace('vehicle_', '')),
            vehicle_id: updatedCar.vehicle_id,
            vehicle_type: updatedCar.vehicle_type,
            route_sequence: updatedCar.route_sequence || customerSteps,
            raw_shapes: updatedCar.raw_shapes || [],
            raw_shape: updatedCar.raw_shape || '',
            distance: updatedCar.distance || 0,
            route_time: updatedCar.route_time || 0,
            start_time: updatedCar.start_time,
            end_time: updatedCar.end_time,
            customers_count: updatedCar.customers_count || 0,
            total_demand: updatedCar.total_demand || 0,
            vehicle_capacity: updatedCar.vehicle_capacity || 0,
            customers: customerSteps.map((step: any) => step.customer_id).filter((id: any) => id !== undefined),
            totalTime: updatedCar.route_time || 0,
            totalDistance: updatedCar.distance || 0,
          };
        }

        setRoutes(updatedRoutes);
        setSelectedRoute(updatedRoutes[routeIndex]);
        showToast('success', 'Rota başarıyla güncellendi');
      } else {
        console.error('❌ Route update failed:', response.error);
        showToast('error', 'Rota güncellenemedi: ' + (response.error || 'Bilinmeyen hata'));
      }
    } catch (error: any) {
      console.error('💥 Error updating route:', error);
      showToast('error', 'Rota güncellenirken hata oluştu');
    }
  };

  const optimizeWithAI = async () => {
    if (!selectedRoute) {
      showToast('warning', 'Lütfen bir rota seçin');
      return;
    }

    try {
      setLoading(true);
      showToast('info', 'AI ile rota optimize ediliyor...');

      const route = selectedRoute;
      const customerSteps = route.route_sequence?.filter((s: any) =>
        s && (s.location_type === 'CUSTOMER' || s.location_type === 'customer')
      ) || [];

      if (customerSteps.length === 0) {
        showToast('error', 'Rota üzerinde müşteri bulunamadı');
        return;
      }

      const depotStr = localStorage.getItem('mobiroute_depot');
      const depotData = depotStr ? JSON.parse(depotStr) : depot;

      const customerLocations = customerSteps.map((step: any) => {
        const customer = selectedCustomers.find(c =>
          c.id === step.customer_id || c.name === step.customer_name || c.name === step.name
        );

        const lat = step.lat !== undefined ? step.lat :
                   step.latitude !== undefined ? step.latitude :
                   customer?.lat;
        const lon = step.lon !== undefined ? step.lon :
                   step.longitude !== undefined ? step.longitude :
                   customer?.lon;

        if (lat === undefined || lon === undefined) {
          throw new Error(`Müşteri için koordinat bilgisi eksik: ${step.customer_name || step.name}`);
        }

        const index = customerSteps.indexOf(step) + 1;
        return {
          id: step.customer_id || step.id || '',
          lat: lat,
          lon: lon,
          demand: step.demand || customer?.demand || 0,
          name: step.customer_name || step.name || customer?.name || `Müşteri ${index}`
        };
      });

      const depotLocation = {
        id: 'depot_1',
        lat: depotData?.lat || depot.lat,
        lon: depotData?.lon || depot.lon,
        name: 'Depo'
      };

      const aiRequest = {
        vehicle_id: route.vehicle_id,
        depot_location: depotLocation,
        customer_locations: customerLocations
      };

      console.log('🤖 Sending AI optimization request:', aiRequest);

      const response = await vrpApiService.optimizeRouteWithAI(aiRequest);

      if (response.data && response.data.status === 'success' && response.data.cars) {
        console.log('🤖 AI optimization successful:', response.data);

        const updatedCars = response.data.cars;
        const updatedCar = updatedCars.find((car: any) => car.vehicle_id === selectedRoute.vehicle_id);

        if (!updatedCar) {
          throw new Error('Güncellenmiş rota bilgisi bulunamadı');
        }

        const allRoutes = [...routes];
        const routeIndex = allRoutes.findIndex(r => r.vehicle_id === selectedRoute.vehicle_id);

        if (routeIndex !== -1) {
          let rawShapes = updatedCar.raw_shapes || [];
          if (!rawShapes.length && updatedCar.raw_shape) {
            rawShapes = [updatedCar.raw_shape];
          }

          const updatedRouteSequence = updatedCar.route_sequence || customerSteps;

          const updatedCustomerIds = updatedRouteSequence
            .filter((s: any) => s && (s.location_type === 'CUSTOMER' || s.location_type === 'customer'))
            .map((step: any) => step.customer_id || step.id)
            .filter((id: any) => id !== undefined);

          allRoutes[routeIndex] = {
            ...allRoutes[routeIndex],
            ...updatedCar,
            raw_shapes: rawShapes,
            route_sequence: updatedRouteSequence,
            customers: updatedCustomerIds,
            customers_count: updatedCustomerIds.length,
            distance: updatedCar.distance || allRoutes[routeIndex].distance,
            route_time: updatedCar.route_time || allRoutes[routeIndex].route_time,
            total_demand: updatedCar.total_demand || allRoutes[routeIndex].total_demand
          };

          setRoutes(allRoutes);
          setSelectedRoute(allRoutes[routeIndex]);

          showToast('success', 'Rota AI ile başarıyla optimize edildi');
        } else {
          console.error('❌ Rota bulunamadı:', selectedRoute.vehicle_id);
          showToast('error', 'Optimize edilen rota bulunamadı');
        }
      } else {
        console.error('❌ AI optimization failed:', response.error || response.data);
        showToast('error', 'AI optimizasyonu başarısız oldu: ' + (response.error || response.data?.message || 'Bilinmeyen hata'));
      }
    } catch (error: any) {
      console.error('💥 Error in AI optimization:', error);
      showToast('error', 'AI optimizasyonu sırasında hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRouteClick = (routeOrVehicleId: Route | string) => {
    // Accept both route object and vehicleId for backward compatibility
    const route = typeof routeOrVehicleId === 'string'
      ? routes.find(r => r.vehicle_id === routeOrVehicleId)
      : routeOrVehicleId;

    if (route) {
      setSelectedRoute(route);
      setManuallyClosedRoute(false); // Reset manual close flag when selecting a route
    }
  };

  const closeSelectedRoute = () => {
    setSelectedRoute(null);
    setManuallyClosedRoute(true); // Mark as manually closed
  };

  return {
    routes,
    setRoutes,
    selectedRoute,
    setSelectedRoute,
    closeSelectedRoute,
    solutionStats,
    loading,
    solverType,
    setSolverType,
    solveVRP,
    reorderCustomers,
    optimizeWithAI,
    handleRouteClick
  };
};
